import { Popup } from 'maplibre-gl';
import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import PoiPopup from '../../components/map/poi-popup';
import { createImageMarker, createNewImageMarker, getAnchorAndOffset } from '../../utils';
import { useLayers } from './layers';
import { usePopup } from './popup';
import { arrivalLayerId, wayPointsLayerId } from './routing';
import { useSource } from './source';
const sourceId = 'pois';
export const poisLayerId = 'pois';
export const poisMinZoom = 13;
const _minZoom = poisMinZoom;
const _categoryMinZoom = {};
const _asMarkerMinZoom = 14;
const _categoryAsMarkerMinZoom = {};
export function usePois(map, theme, smallDevice, _categories, _selectedCategories, _poiAnnotationsMap = {}, options = {}) {
  const [selectedPoi, selectPoi] = useState(null);
  const hoveredPoi = useRef(null);
  const initialized = useRef(false);
  const categories = useRef(_categories);
  const selectedCategories = useRef(_selectedCategories);
  const poiAnnotationsMap = useRef(_poiAnnotationsMap);
  const displayOnlyBicycleHome = useRef(options.displayOnlyBicycleHome || false);
  const markers = useRef({});
  const poiTooltip = useRef(null);
  const poisRef = useRef([]);
  const poiPopupRef = useRef(null);
  const selectedPoiIdRef = useRef(null);
  const selectedTripStep = useRef(options.selectedTripStep);
  const snackbarKey = useRef(null);
  const {
    t
  } = useTranslation();
  const {
    getGeoJSONSource,
    addGeoJSONSource,
    updateGeoJSONSource,
    clearGeoJSONSource
  } = useSource(map, sourceId);
  const {
    addCircleLayer
  } = useLayers(map);
  const {
    centerPopup
  } = usePopup(map);
  const minZoom = options.minZoom || _minZoom;
  const categoryMinZoom = {
    ..._categoryMinZoom,
    ...(options.categoryMinZoom || {})
  };
  const asMarkerMinZoom = options.asMarkerMinZoom || _asMarkerMinZoom;
  const categoryAsMarkerMinZoom = {
    ..._categoryAsMarkerMinZoom,
    ...(options.categoryAsMarkerMinZoom || {})
  };
  useEffect(() => {
    categories.current = _categories;
  }, [_categories]);
  useEffect(() => {
    selectedCategories.current = _selectedCategories;
  }, [_selectedCategories]);
  useEffect(() => {
    poiAnnotationsMap.current = _poiAnnotationsMap;
  }, [_poiAnnotationsMap]);
  useEffect(() => {
    displayOnlyBicycleHome.current = options.displayOnlyBicycleHome || false;
  }, [options.displayOnlyBicycleHome]);
  useEffect(() => {
    const {
      selectedTripStep: step
    } = options;
    selectedTripStep.current = step;
    if (_poiAnnotationsMap && step) poiPopupRef.current?.update(_poiAnnotationsMap, step);
  }, [_poiAnnotationsMap, options.selectedTripStep]);
  function getCategoriesMap() {
    return categories.current?.reduce((res, {
      code,
      color,
      icon
    }) => {
      res[code] = {
        color,
        icon
      };
      return res;
    }, {}) || {};
  }
  function init() {
    if (!map || initialized.current) return;
    if (getGeoJSONSource()) {
      initialized.current = true;
      return;
    }
    addGeoJSONSource();
    addCircleLayer(poisLayerId, sourceId, {
      'circle-radius': 4,
      'circle-stroke-width': 2,
      'circle-stroke-opacity': 1,
      'circle-color': ['get', 'color'],
      'circle-stroke-color': '#fff'
    });
    map.on('mouseenter', poisLayerId, event => {
      if (event.defaultPrevented) return;
      event.originalEvent.preventDefault();
      event.preventDefault();
      map.getCanvas().style.cursor = 'pointer';
      const {
        point,
        features
      } = event;
      if (map.queryRenderedFeatures(point).find(({
        layer: {
          id: layerId
        }
      }) => [arrivalLayerId, wayPointsLayerId].indexOf(layerId) !== -1) || !features || !features[0]) return;
      const {
        id,
        geometry,
        properties
      } = features[0];
      if (selectedPoiIdRef.current === id) return;
      if (typeof id === 'number' && geometry.type === 'Point') {
        const {
          coordinates: [lng, lat]
        } = geometry;
        openTooltip(id, {
          lat,
          lng
        }, properties?.title || null, 7);
      }
    });
    map.on('mousemove', poisLayerId, event => {
      const {
        point,
        features
      } = event;
      if (map.queryRenderedFeatures(point).find(({
        layer: {
          id: layerId
        }
      }) => [arrivalLayerId, wayPointsLayerId].indexOf(layerId) !== -1) || !features || !categories.current || !features[0]) return;
      const {
        id
      } = features[0];
      const selectedPoi = poisRef.current.find(poi => poi.id === id);
      if (selectedPoi && selectedPoi !== hoveredPoi.current) {
        if (hoveredPoi.current) {
          markers.current[hoveredPoi.current.id]?.marker.remove();
        }
        hoveredPoi.current = selectedPoi;
        if (hoveredPoi.current) {
          addMarker(map.getZoom(), getCategoriesMap(), hoveredPoi.current, {
            forced: true
          });
        }
      }
    });
    map.on('mouseleave', poisLayerId, () => {
      if (map) map.getCanvas().style.cursor = '';
      clearTooltip();
    });
    map.on('click', poisLayerId, event => {
      if (event.originalEvent.defaultPrevented) return;
      event.originalEvent.preventDefault();
      const {
        features
      } = event;
      if (features && features.length > 0) {
        const {
          id: poiId,
          geometry,
          properties
        } = features[0];
        const zoom = properties?.asMarkerMinZoom;
        if (typeof poiId === 'number') {
          const poi = poisRef.current.find(({
            id
          }) => id === poiId);
          if (poi) void selectPoiOnClick(poi, false);
        }
        if (zoom && geometry.type === 'Point') {
          const {
            coordinates: [lng, lat]
          } = geometry;
          map.flyTo({
            center: {
              lat,
              lng
            },
            zoom
          });
        }
      }
    });
    initialized.current = true;
  }
  function openPoiPopup(poi) {
    closePrevSelectedPoiPopup();
    if (smallDevice) return;
    const {
      location: {
        point: {
          coordinates: [lng, lat]
        }
      }
    } = poi;
    const selectedMarker = selectedPoiIdRef.current && markers.current[selectedPoiIdRef.current];
    if (!selectedMarker || !categories.current) {
      unselect();
      return;
    }
    const popup = selectedMarker.getPopup();
    if (popup.isOpen()) return;
    selectedMarker.marker.setPopup(popup);
    selectedMarker.marker.togglePopup();
    const ele = document.getElementById(`poi-${poi.id}-popup`);
    if (ele) {
      const container = createRoot(ele);
      container.render(/*#__PURE__*/React.createElement(PoiPopup, {
        categories: categories.current,
        enqueueSnackbar: options.enqueueSnackbar,
        onAddAsRideStep: options.onAddAsRideStep,
        onClose: () => {
          if (popup.isOpen()) selectedMarker.marker.togglePopup();
          if (container) container.unmount();
        },
        onPoiAddedToTripStep: options.onPoiAddedToTripStep,
        onPoiRemovedFromTripStep: options.onPoiRemovedFromTripStep,
        onRemoveFromRideSteps: options.onRemoveFromRideSteps,
        onSearchUpdated: options.onSearchUpdated,
        onTripStepAccomodationSelected: options.onTripStepAccomodationSelected,
        onTripStepAccomodationUnselected: options.onTripStepAccomodationUnselected,
        poi: poi,
        poiAnnotationsMap: poiAnnotationsMap.current,
        ref: poiPopupRef,
        search: options.search,
        selectedTripStep: selectedTripStep.current,
        theme: theme,
        widgetConfig: options.widgetConfig
      }));
    }
    setTimeout(() => centerPopup({
      lat,
      lng
    }, popup), 100);
  }
  async function selectPoiOnClick(poi, openPopup) {
    selectedPoiIdRef.current = poi.id;
    closePrevSelectedPoiPopup();
    try {
      const detailedPoi = (await options.onPoiClick?.(poi)) || poi;
      if (selectedPoiIdRef.current !== poi.id) return;
      poisRef.current = poisRef.current.map(currentPoi => currentPoi.id === poi.id ? detailedPoi : currentPoi);
      selectPoi(detailedPoi);
      if (openPopup) openPoiPopup(detailedPoi);
    } catch (err) {
      if (selectedPoiIdRef.current === poi.id) unselect();
      console.error(err);
    }
  }
  function closePrevSelectedPoiPopup() {
    Object.values(markers.current).forEach(({
      id,
      marker
    }) => {
      const popup = marker.getPopup();
      if (id !== selectedPoiIdRef.current && popup?.isOpen()) marker.togglePopup();
    });
  }
  function addMarker(zoom, categoriesMap, poi, {
    forced
  } = {}) {
    if (!map) return;
    const {
      id,
      title,
      source,
      location: {
        point: {
          coordinates: [lng, lat]
        }
      },
      categoryCodes
    } = poi;
    const categoryCode = forced ? categoryCodes[0] : categoryCodes.find(code => selectedCategories.current[code] && zoom >= (categoryAsMarkerMinZoom[code] || asMarkerMinZoom));
    function onClick() {
      const markerMinZoom = categoryCode && categoryAsMarkerMinZoom[categoryCode] || asMarkerMinZoom;
      clearTooltip();
      if (map && map.getZoom() < markerMinZoom) {
        const {
          coordinates: [lng, lat]
        } = poi.location.point;
        hoveredPoi.current = null;
        map.flyTo({
          center: {
            lat,
            lng
          },
          zoom: markerMinZoom
        });
      } else {
        if (selectedPoiIdRef.current !== id) {
          void selectPoiOnClick(poi, true);
        }
      }
    }
    function onMouseEnter() {
      openTooltip(id, {
        lat,
        lng
      }, title, 12);
    }
    function onMouseLeave() {
      if (hoveredPoi.current) {
        markers.current[hoveredPoi.current.id]?.marker.remove();
        hoveredPoi.current = null;
      }
      clearTooltip();
    }
    const {
      useNewImageMarkers
    } = options;
    let popup = null;
    function getPopup() {
      if (!popup) {
        popup = new Popup({
          maxWidth: '350px',
          closeButton: false,
          closeOnClick: true,
          className: 'map-custom-popup',
          offset: useNewImageMarkers ? [0, -25] : 8
        }).setHTML(`<div id="poi-${id}-popup"></div>`);
        popup.on('close', () => {
          if (selectedPoiIdRef.current === id && markers.current[id]) {
            unselect();
          }
        });
      }
      return popup;
    }
    const marker = (useNewImageMarkers ? createNewImageMarker({
      color: categoryCode && categoriesMap[categoryCode]?.color || '#000',
      icon: categoryCode && categoriesMap[categoryCode]?.icon || '',
      height: 30
    }, {
      onClick,
      onMouseEnter,
      onMouseLeave
    }) : createImageMarker({
      radius: 10,
      borderWidth: 2,
      backgroundColor: categoryCode && categoriesMap[categoryCode]?.color || '#000',
      backgroundImage: categoryCode && categoriesMap[categoryCode]?.icon || '',
      backgroundSize: 14,
      zIndex: selectedPoiIdRef.current === id ? '2' : '1'
    }, {
      onClick,
      onMouseEnter,
      onMouseLeave
    })).setLngLat({
      lat,
      lng
    }).addTo(map);
    markers.current[id] = {
      id,
      source,
      marker,
      getPopup
    };
  }
  function update(pois, {
    source: _source,
    forcedAsMarker,
    alwaysVisible
  } = {}) {
    const source = _source || 'geovelo';
    poisRef.current = pois;
    if (!map || !categories.current) return;
    const zoom = map.getZoom();
    const bounds = map.getBounds();
    const categoriesMap = getCategoriesMap();
    const filteredPois = pois.filter(({
      id,
      categoryCodes,
      bicycleHome,
      location: {
        point: {
          coordinates: [lng, lat]
        }
      },
      externalId,
      filterByBounds
    }) => forcedAsMarker?.find(_id => externalId ? _id === externalId : _id === id) || (!filterByBounds || bounds.contains({
      lat,
      lng
    })) && categoryCodes.find(code => selectedCategories.current[code]) && (!displayOnlyBicycleHome.current || bicycleHome));
    const count = filteredPois.length;
    const filteredPoisAsMarker = filteredPois.filter(({
      id,
      categoryCodes,
      externalId
    }) => forcedAsMarker?.find(_id => externalId ? _id === externalId : _id === id) || categoryCodes.find(code => zoom >= (categoryAsMarkerMinZoom[code] || asMarkerMinZoom)));
    const filteredPoisAsMarkerIds = filteredPoisAsMarker.reduce((res, {
      id
    }) => {
      res[id] = true;
      return res;
    }, {});

    // remove markers that are no longer visible
    Object.values(markers.current).filter(({
      id,
      source: poiSource
    }) => poiSource === source && !filteredPoisAsMarkerIds[id]).forEach(({
      id,
      marker
    }) => {
      marker.remove();
      delete markers.current[id];
    });

    // unselect poi if no longer visible
    if (selectedPoiIdRef.current && !markers.current[selectedPoiIdRef.current] && !alwaysVisible) {
      unselect();
    }

    // add markers that were not visible
    filteredPoisAsMarker.filter(({
      id
    }) => !markers.current[id]).forEach(poi => addMarker(zoom, categoriesMap, poi, {
      forced: !!forcedAsMarker?.find(id => poi.externalId ? id === poi.externalId : id === poi.id)
    }));

    // close previous selected poi popup
    closePrevSelectedPoiPopup();

    // open selected poi popup
    const selectedPoi = selectedPoiIdRef.current && pois.find(({
      id
    }) => selectedPoiIdRef.current === id);
    if (selectedPoi) openPoiPopup(selectedPoi);
    const filteredPoisAsPoints = filteredPois.filter(({
      id,
      categoryCodes
    }) => !filteredPoisAsMarkerIds[id] && (alwaysVisible || categoryCodes.find(code => zoom >= (categoryMinZoom[code] || minZoom))));
    const visibleCount = filteredPoisAsMarker.length + filteredPoisAsPoints.length;
    updateGeoJSONSource({
      type: 'FeatureCollection',
      features: filteredPoisAsPoints.map(poi => {
        const categoryCode = poi.categoryCodes.find(code => selectedCategories.current[code] && (alwaysVisible || zoom > (categoryMinZoom[code] || minZoom)));
        const category = categories.current?.find(({
          code
        }) => code === categoryCode);
        return {
          id: poi.id,
          type: 'Feature',
          geometry: poi.location.point,
          properties: {
            title: poi.title || (category?.labelKey ? t(category.labelKey, {
              context: 'one'
            }) : ''),
            asMarkerMinZoom: categoryCode && categoryAsMarkerMinZoom[categoryCode] || asMarkerMinZoom,
            color: categoryCode && categoriesMap[categoryCode]?.color || '#000'
          }
        };
      })
    });
    if (options.displayZoomInSnackbar) {
      options.closeSnackbar?.(snackbarKey.current || undefined);
      if (count > 0 && visibleCount < count) snackbarKey.current = options.enqueueSnackbar?.(t('commons.actions.zoom_in_to_display_pois', {
        count
      })) || null;
    }
  }
  function select(poi, {
    openPopup,
    disableAnimation
  } = {}) {
    if (!map) return;
    const {
      id,
      location: {
        point: {
          coordinates: [lng, lat]
        }
      },
      categoryCodes
    } = poi;
    const zoom = map.getZoom();
    const categoryCode = categoryCodes.find(code => selectedCategories.current[code] && zoom > (categoryMinZoom[code] || minZoom));
    selectedPoiIdRef.current = id;
    selectPoi(poi);
    if (openPopup) openPoiPopup(poi);else {
      if (!markers.current[id]) addMarker(zoom, getCategoriesMap(), poi, {
        forced: true
      });
      map.flyTo({
        center: {
          lat,
          lng
        },
        zoom: categoryCode && categoryAsMarkerMinZoom[categoryCode] || asMarkerMinZoom,
        animate: !disableAnimation
      });
    }
  }
  function unselect() {
    selectedPoiIdRef.current = null;
    selectPoi(null);
    closePrevSelectedPoiPopup();
  }
  function openTooltip(id, location, title, _offset = 0) {
    clearTooltip();
    if (!map || !title || selectedPoiIdRef.current === id) return;
    const {
      anchor,
      offset
    } = getAnchorAndOffset(map, location, _offset);
    poiTooltip.current = new Popup({
      anchor,
      offset: options.useNewImageMarkers && Array.isArray(offset) && [offset[0], -15] || 0,
      className: 'map-tooltip',
      closeButton: false
    }).setHTML(title).setLngLat(location).addTo(map);
  }
  function clearTooltip() {
    poiTooltip.current?.remove();
  }
  function clear() {
    poisRef.current = [];
    Object.values(markers.current).forEach(({
      marker
    }) => {
      marker.remove();
    });
    markers.current = {};
    unselect();
    clearTooltip();
    clearGeoJSONSource();
  }
  return {
    initialized: initialized.current,
    selectedPoi,
    init,
    update,
    select,
    unselect,
    clear
  };
}